
import { useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import { useLogger } from '../contexts/LogContext';
import { LogLevel, LogCategory } from '../types/logs';

export const useWalletLogin = () => {
    const { connectWallet, setIsNewWeb3User } = useAuth();
    const { address } = useWallet();
    const { addLog } = useLogger();
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const processWalletLogin = useCallback(async () => {
        if (!address) {
            addLog('processWalletLogin called without a wallet address.', LogLevel.WARN, LogCategory.AUTH);
            return null;
        }

        setIsLoggingIn(true);
        addLog('Starting wallet login process...', LogLevel.INFO, LogCategory.AUTH, { walletAddress: address });

        try {
            const existingUser = await connectWallet(address);

            if (existingUser) {
                // User is an existing user. The connectWallet function has logged them in.
                // The AuthProvider's listener will now handle redirecting them to the dashboard.
                addLog('Wallet user found. Login successful.', LogLevel.INFO, LogCategory.AUTH, { userId: existingUser.id });
                return null; // No immediate redirect needed from the caller.
            } else {
                // This is a new user.
                addLog('New wallet user detected. Redirecting to registration form.', LogLevel.INFO, LogCategory.AUTH, { walletAddress: address });
                setIsNewWeb3User(true);
                // We return the path for the UI to navigate to.
                return 'join_request';
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error during wallet login';
            addLog(`Error during wallet login processing: ${errorMessage}`, LogLevel.ERROR, LogCategory.AUTH, { walletAddress: address });
            return null; // On error, do not redirect.
        } finally {
            setIsLoggingIn(false);
        }
    }, [address, connectWallet, addLog, setIsNewWeb3User]);

    return { processWalletLogin, isLoggingIn };
};
